import React from "react";
import { connect } from "react-redux";
import { useIntl, FormattedMessage } from "react-intl";

import { TranslationsProvider } from "@learningblocks/simulations/shared-components";

import { fetchTranslations } from "./locales/config";
import styles from "./styles.module.scss";

/**
 * Renders a choice with optional arrow indicators on top or bottom.
 *
 * @param {Object} props - The props for the component.
 * @param {string} props.type - The type of the choice, determining the styling.
 * @param {boolean} props.arrowTop - Flag to display an arrow on top.
 * @param {boolean} props.arrowBottom - Flag to display an arrow on the bottom.
 * @param {string} props.text - The text to be displayed in the choice.
 */
const ChoiceWrapper = ({ type, arrowTop, arrowBottom, text }) => (
  <div
    className={
      type === "label"
        ? styles.choiceWrapperLabel
        : styles.choiceWrapperSelectedChoice
    }
    style={{ display: text ? "flex" : "none" }}
  >
    <div className={styles.arrowTail}>
      {arrowTop && <div className={styles.arrowTailArrowTop}></div>}
      {arrowBottom && <div className={styles.arrowTailArrowBottom}></div>}
    </div>
    <div className={styles.text}>{text}</div>
    <div className={styles.arrowHead}></div>
  </div>
);

/**
 * Displays questions and choices based on the given array of choices.
 *
 * @param {Object} props - The props for the component.
 * @param {Array<string>} props.choices - Array of choices to be displayed.
 */
const ResultsQuestions59 = (props) => {
  const intl = useIntl();

  const choices =
    props.component_type == "ideal"
      ? [
          intl.formatMessage({
            id: "Ampicillin kills the non-transformed bacteria",
            defaultMessage: "Ampicillin kills the non-transformed bacteria",
            description: "",
          }),
          intl.formatMessage({
            id: "The bacteria did not start out antibiotic sensitive",
            defaultMessage:
              "The bacteria did not start out antibiotic sensitive",
            description: "",
          }),
          intl.formatMessage({
            id: "The transformation did not work",
            defaultMessage: "The transformation did not work",
            description: "",
          }),
          intl.formatMessage({
            id: "The transformation worked",
            defaultMessage: "The transformation worked",
            description: "",
          }),
        ]
      : props.choices;

  // Helper function to render a question item
  const renderQuestion = (labelText, choiceText) => (
    <div className={styles.itemFlex}>
      <ChoiceWrapper type="label" arrowTop arrowBottom text={labelText} />
      <ChoiceWrapper
        type="selectedChoice"
        arrowTop
        arrowBottom
        text={choiceText}
      />
    </div>
  );

  return (
    <div className={styles.predictionResults}>
      <h5 className={styles.label}>
        <FormattedMessage
          id="P- HALF"
          defaultMessage="P- HALF"
          description=""
        />
      </h5>
      <div className={styles.choiceWrapper}>
        <h6 className={styles.questionLabel}>
          <FormattedMessage
            id="QUESTION 1"
            defaultMessage="QUESTION 1"
            description=""
          />
        </h6>
        {renderQuestion(
          intl.formatMessage({
            id: "There is no growth on the P- half",
            defaultMessage: "There is no growth on the P- half",
            description: "",
          }),
          choices[0]
            ? intl.formatMessage({
                id: choices[0],
                defaultMessage: choices[0],
                description: "",
              })
            : choices[0],
          0
        )}
        <h6 className={styles.questionLabel}>
          <FormattedMessage
            id="QUESTION 2"
            defaultMessage="QUESTION 2"
            description=""
          />
        </h6>
        {renderQuestion(
          intl.formatMessage({
            id: "There is some growth on the P- half",
            defaultMessage: "There is some growth on the P- half",
            description: "",
          }),
          choices[1]
            ? intl.formatMessage({
                id: choices[1],
                defaultMessage: choices[1],
                description: "",
              })
            : choices[1],
          1
        )}
      </div>

      <h5 className={styles.label}>
        <FormattedMessage
          id="P+ HALF"
          defaultMessage="P+ HALF"
          description=""
        />
      </h5>
      <div className={styles.choiceWrapper}>
        <h6 className={styles.questionLabel}>
          <FormattedMessage
            id="QUESTION 1"
            defaultMessage="QUESTION 1"
            description=""
          />
        </h6>
        {renderQuestion(
          intl.formatMessage({
            id: "There is no growth on the P+ half",
            defaultMessage: "There is no growth on the P+ half",
            description: "",
          }),
          choices[2]
            ? intl.formatMessage({
                id: choices[2],
                defaultMessage: choices[2],
                description: "",
              })
            : choices[2],
          2
        )}
        <h6 className={styles.questionLabel}>
          <FormattedMessage
            id="QUESTION 2"
            defaultMessage="QUESTION 2"
            description=""
          />
        </h6>
        {renderQuestion(
          intl.formatMessage({
            id: "There is some growth on the P+ half",
            defaultMessage: "There is some growth on the P+ half",
            description: "",
          }),
          choices[3]
            ? intl.formatMessage({
                id: choices[3],
                defaultMessage: choices[3],
                description: "",
              })
            : choices[3],
          3
        )}
      </div>
    </div>
  );
};

/**
 * Maps Redux state to component props.
 *
 * @param {Object} state - The current state of the Redux store.
 * @param {Object} ownProps - The props passed to the component.
 * @returns {Object} The mapped props.
 */
const mapStateToProps = (state, ownProps) => {
  // Check if the component type is "ideal" and return hardcoded choices
  if (ownProps.component_type === "ideal") {
    return {
      choices: [],
    };
  }
  // Otherwise, return choices based on the Redux state of predictions
  return {
    choices: [
      state.poscq[1] ? state.poscq[1].label : "",
      state.poscq[2] ? state.poscq[2].label : "",
      state.poscq[3] ? state.poscq[3].label : "",
      state.poscq[4] ? state.poscq[4].label : "",
    ],
  };
};

export default connect(mapStateToProps)((props) => (
  <TranslationsProvider fetchTranslations={fetchTranslations}>
    <ResultsQuestions59 {...props} />
  </TranslationsProvider>
));
